import type {
  ChangeEventHandler,
  FC,
  KeyboardEventHandler,
  MouseEvent,
  RefObject
} from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { Picture } from '@ui';
import icons from '@icons';
import type {
  DadataSuggestion,
  GetMapSuggestionsSuggestArgs,
  GetMapSuggestionsSuggestArgsLocation
} from '@api/dadata';
import DadataAPI from '@api/dadata';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import usePreviousValue from '@customHooks/usePreviousValue';
import './react-dadata.css';
import { useStores } from '@root/src/stores';

const {
  search: { darkGrey: searchIconOutlinedDarkGrey },
  close: { midGrey: closeOutlinedMidGrey }
} = icons.Outline;

interface Address {
  data: {
    region_fias_id: string;
    city_fias_id: string;
    settlement_fias_id: string;
    street_fias_id: string;
  };
}

export interface DadataProps {
  fromBound?: string;
  toBound?: string;
  validate?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  disabled?: boolean;
  autocomplete?: boolean;
  placeholder?: string;
  query?: string;
  name?: string;
  address?: Address;
  autoload?: boolean;
  count?: number;
  payload?: Partial<GetMapSuggestionsSuggestArgs>;
  className?: string;
  isNewStyleSearch?: boolean;
  onChange?: (suggestion: DadataSuggestion) => void;
}

const Dadata: FC<DadataProps> = observer(
  ({
    toBound,
    validate,
    onBlur,
    onFocus,
    disabled,
    autocomplete,
    placeholder,
    query: queryProps,
    name,
    address,
    autoload,
    fromBound = 'city',
    className,
    isNewStyleSearch,
    onChange,
    count = 5,
    payload
  }) => {
    const { cartStore } = useStores();
    const regionGeoSuggestions = cartStore.regionData?.fields?.geo_suggestions;
    const effectivePayload = useMemo<Partial<GetMapSuggestionsSuggestArgs>>(
      () =>
        payload ?? {
          from_bound: {
            value: 'city'
          },
          restrict_value: false,
          locations: regionGeoSuggestions?.length
            ? regionGeoSuggestions.map(id => ({
                kladr_id: String(id)
              }))
            : [{ kladr_id: '50' }, { kladr_id: '77' }]
        },
      [payload, regionGeoSuggestions]
    );
    const [query, setQuery] = useState(queryProps || '');
    const [inputQuery, setInputQuery] = useState(queryProps || '');
    const [inputFocused, setInputFocused] = useState(false);
    const [isShouldBlur, setIsShouldBlur] = useState(true);
    const [suggestions, setSuggestions] = useState<DadataSuggestion[]>([]);
    const [suggestionIndex, setSuggestionIndex] = useState(-1);
    const [suggestionsVisible, setSuggestionsVisible] = useState(true);

    const textInputRef = useRef<HTMLInputElement | null>(null);

    const timerBlurRef = useRef<NodeJS.Timeout | null>(null);

    const prevQueryProps = usePreviousValue(queryProps);

    useEffect(() => {
      if (prevQueryProps !== queryProps) {
        setQuery(queryProps || '');
        setInputQuery(queryProps || '');
      }
    }, [prevQueryProps, queryProps]);

    const fetchSuggestions = useCallback(
      (query: string, count?: number): void => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort('Request cancelled');
        }

        abortControllerRef.current = new AbortController();

        const signal = abortControllerRef.current?.signal;

        const requestPayload: GetMapSuggestionsSuggestArgs = {
          query,
          count: count || 10,
          ...(effectivePayload || {})
        };

        // Checking for granular suggestions
        if (fromBound && toBound) {
          // When using granular suggestion, all dadata components have to receive address property that contains shared address info.
          if (!address) {
            throw new Error(
              'You have to pass address property with DaData address object to connect separate components'
            );
          }
          requestPayload.from_bound = { value: fromBound };
          requestPayload.to_bound = { value: toBound };
          requestPayload.restrict_value = true;

          if (address.data) {
            // Define location limitation

            const location: GetMapSuggestionsSuggestArgsLocation = {};

            if (address.data.region_fias_id) {
              location.region_fias_id = address.data.region_fias_id;
            }
            if (address.data.city_fias_id) {
              location.city_fias_id = address.data.city_fias_id;
            }
            if (address.data.settlement_fias_id) {
              location.settlement_fias_id = address.data.settlement_fias_id;
            }
            if (address.data.street_fias_id) {
              location.street_fias_id = address.data.street_fias_id;
            }
            requestPayload.locations = [location];
          }
        }

        DadataAPI.getMapSuggestionsSuggest(requestPayload, signal)
          .then(res => {
            setSuggestions(res.suggestions);
            setSuggestionIndex(-1);
            setIsShouldBlur(true);
          })
          .catch(error => {
            if (error?.name === 'AbortError') {
              // Request was cancelled, ignore
            }
          });
      },
      [address, fromBound, effectivePayload, toBound]
    );

    const clearBlurTimer = (): void => {
      if (timerBlurRef.current) {
        clearTimeout(timerBlurRef.current);
        timerBlurRef.current = null;
      }
    };

    useEffect(() => {
      if (autoload && query) {
        fetchSuggestions(query, count);
      }
    }, [autoload, count, fetchSuggestions, query]);

    const onInputFocus = useCallback((): void => {
      setInputFocused(true);
      if (suggestions.length === 0) {
        fetchSuggestions(query, count);
      }
      onFocus?.();
    }, [count, fetchSuggestions, onFocus, query, suggestions.length]);

    const sendOnChange = useCallback(
      (item): void => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort('Request cancelled');
        }

        abortControllerRef.current = new AbortController();

        const signal = abortControllerRef.current?.signal;

        const requestPayload = {
          query: item.value,
          count: 1,
          restrict_value: false
        };

        DadataAPI.getMapSuggestionsSuggest(requestPayload, signal).then(res => {
          if (res?.suggestions?.length && onChange) {
            onChange(res.suggestions[0]);
          }
        });
      },
      [onChange]
    );

    const selectSuggestion = useCallback(
      (index: number, autoFocus = true): void => {
        if (index >= 0 && index < suggestions.length) {
          const item = { ...suggestions[index] }; // Clone for safety

          clearBlurTimer();

          setQuery(item.value || '');
          setSuggestionsVisible(false);
          setInputQuery(item.value || '');
          setSuggestions([item]);
          setIsShouldBlur(false);
          if (autoFocus) {
            setTimeout(() => {
              setCursorToEnd(textInputRef.current);
            }, 100);
          }

          if (onChange) {
            sendOnChange(item);
          }
        }
      },
      [onChange, sendOnChange, suggestions]
    );

    const onInputBlur = useCallback((): void => {
      setInputFocused(false);
      if (suggestions.length === 0) {
        fetchSuggestions(query, count);
      } else if (isShouldBlur) {
        timerBlurRef.current = setTimeout(() => {
          selectSuggestion(0, false);
        }, 500);
      }
      onBlur?.();
    }, [
      count,
      fetchSuggestions,
      isShouldBlur,
      onBlur,
      query,
      selectSuggestion,
      suggestions.length
    ]);

    const onInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
      event => {
        const value = event.target.value;
        setQuery(value);
        setInputQuery(value);
        setSuggestionsVisible(true);
        if (validate) {
          validate(value);
        }
        fetchSuggestions(value, count);
      },
      [count, fetchSuggestions, validate]
    );

    const onKeyPress: KeyboardEventHandler<HTMLInputElement> = event => {
      if (event.which === 40) {
        // Arrow down
        event.preventDefault();
        if (suggestionIndex < suggestions.length) {
          const newSuggestionIndex = suggestionIndex + 1;
          const newInputQuery = suggestions?.[newSuggestionIndex]?.value || '';
          setSuggestionIndex(newSuggestionIndex);
          setQuery(newInputQuery);
        }
      } else if (event.which === 38) {
        // Arrow up
        event.preventDefault();
        if (suggestionIndex >= 0) {
          const newSuggestionIndex = suggestionIndex - 1;
          const newInputQuery =
            newSuggestionIndex === -1
              ? inputQuery || ''
              : suggestions[newSuggestionIndex].value || '';

          setSuggestionIndex(newSuggestionIndex);
          setQuery(newInputQuery);
        }
      } else if (event.which === 13) {
        // Enter
        event.preventDefault();

        if (suggestionIndex >= 0) {
          selectSuggestion(suggestionIndex);
          timerBlurRef.current = setTimeout(() => {
            selectSuggestion(suggestionIndex);
          }, 500);
        } else {
          selectSuggestion(0);
          timerBlurRef.current = setTimeout(() => {
            selectSuggestion(0, false);
          }, 500);
        }
      }
    };

    const onSuggestionClick = (index: number, event: MouseEvent): void => {
      event.stopPropagation();
      selectSuggestion(index);
    };

    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(
      () => () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      },
      []
    );

    const setCursorToEnd = (
      element: RefObject<HTMLInputElement | null>['current']
    ): void => {
      if (!element) {
        return;
      }
      const valueLength = element.value.length;
      if (element?.selectionStart || `${element.selectionStart}` === '0') {
        // Firefox/Chrome
        element.selectionStart = valueLength;
        element.selectionEnd = valueLength;
        element.focus();
      }
    };

    const getHighlightWords = useMemo((): string[] => {
      const wordsToPass = [
        'г',
        'респ',
        'ул',
        'р-н',
        'село',
        'деревня',
        'поселок',
        'пр-д',
        'пл',
        'к',
        'кв',
        'обл',
        'д'
      ];
      let words: string[] = inputQuery.replace(',', '').split(' ');
      words = words.filter(word => !wordsToPass.includes(word));
      return words;
    }, [inputQuery]);

    const onClickCross = (): void => {
      setQuery('');
      setInputQuery('');
      setSuggestionsVisible(true);
    };

    return (
      <div className='react-dadata react-dadata__container'>
        <div>
          {isNewStyleSearch && (
            <Picture
              pictureClassName='searchDadataPicture'
              icon={searchIconOutlinedDarkGrey}
              height='24'
              width='24'
            />
          )}
          <input
            className={clsx(
              'react-dadata__input',
              className,
              isNewStyleSearch ? ' searchDadataInput' : ''
            )}
            disabled={disabled}
            placeholder={placeholder || ''}
            value={query}
            ref={textInputRef}
            onChange={onInputChange}
            onKeyPress={onKeyPress}
            onKeyDown={onKeyPress}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            autoComplete={autocomplete ? 'on' : 'off'}
            name={name}
          />
          {isNewStyleSearch && (
            <Picture
              onClick={onClickCross}
              pictureClassName='closeDadataPicture'
              icon={closeOutlinedMidGrey}
              height='24'
              width='24'
            />
          )}
        </div>
        {inputFocused &&
          suggestionsVisible &&
          suggestions &&
          suggestions.length > 0 && (
            <div className='react-dadata__suggestions'>
              <div className='react-dadata__suggestion-note'>
                Выберите вариант или продолжите ввод
              </div>
              {suggestions?.map((suggestion, index) => {
                let suggestionClassName = 'react-dadata__suggestion';
                if (index === suggestionIndex) {
                  suggestionClassName += ' react-dadata__suggestion--current';
                }
                return (
                  <div
                    key={suggestion.value}
                    onMouseDown={e => {
                      onSuggestionClick(index, e);
                    }}
                    className={suggestionClassName}
                  >
                    <Highlighter
                      highlightClassName='react-dadata--highlighted'
                      autoEscape={true}
                      searchWords={getHighlightWords}
                      textToHighlight={suggestion.value}
                    />
                  </div>
                );
              })}
            </div>
          )}
      </div>
    );
  }
);

export default Dadata;
